import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { XCircle } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useProperty } from "../lib/PropertyContext";
import PropertySwitcher from "../components/customer/PropertySwitcher";
import { STATUS_COLORS } from "../lib/bookingStatus";
import { deDate } from "../lib/dateFormat";
import ServicePackageBooker, { PACKAGES } from "../components/customer/ServicePackageBooker";
import ServicePackageCancelDialog from "../components/customer/ServicePackageCancelDialog";

export default function CustomerServicePackages() {
  const { lang } = useI18n();
  const de = lang === "de";
  const { properties, selected } = useProperty();
  const multi = (properties || []).length > 1;

  const [bookings, setBookings] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [cancelTarget, setCancelTarget] = useState(null);

  const loadMine = async () => {
    try {
      const { data } = await api.get("/service-packages/mine");
      setBookings(data || []);
    } catch (e) { console.debug("service-packages/mine skipped:", e); }
  };
  const loadAbsences = async () => {
    try {
      const { data } = await api.get("/bookings");
      setAbsences(data || []);
    } catch (e) { console.debug("bookings skipped:", e); }
  };
  useEffect(() => { loadMine(); loadAbsences(); }, []);

  // Objekt-spezifisch: die jüngste Buchung für das aktuell gewählte Objekt.
  const lastBooking = useMemo(
    () => (bookings || []).find((b) => b.property_id === selected?.id) || null,
    [bookings, selected?.id]
  );

  // Deep-Link aus der Aufenthalts-Kachel (Dashboard): `?package=<id>` wählt das
  // empfohlene Paket direkt vor. Parameter danach verbrauchen, damit ein
  // späterer Objektwechsel die Auswahl nicht erneut erzwingt.
  const [searchParams, setSearchParams] = useSearchParams();
  const [presetPackageId, setPresetPackageId] = useState(null);
  useEffect(() => {
    const pid = searchParams.get("package");
    if (!pid) return;
    if (PACKAGES.some((p) => p.id === pid)) setPresetPackageId(pid);
    const next = new URLSearchParams(searchParams);
    next.delete("package");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const lbPkg = lastBooking ? PACKAGES.find((p) => p.id === lastBooking.package_id) : null;
  const pkgTitle = (p) => (p ? (de ? p.title : p.title_en) : "");
  const pkgMain = (p) => (p ? (de ? p.main : p.main_en) : "");
  const propName = selected?.name || "";

  // Kunde darf nur das eigene, fortlaufende Paket 1 kündigen.
  const canCancelLast = lastBooking && lastBooking.package_id === "1" && lastBooking.status === "booked";

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="customer-service-packages">
      <div className="mb-10">
        <div className="label-caps mb-3">{de ? "Ihre Betreuung" : "Your service"}</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy">
          {de ? "Service Pakete" : "Service Packages"}
        </h1>
        <p className="text-[15px] text-navy-dark/70 mt-3 max-w-2xl">
          {multi
            ? (de
                ? "Wählen Sie ein Betreuungspaket — jedes Objekt wird separat gebucht. Wechseln Sie oben das Objekt, um für ein weiteres Objekt zu buchen."
                : "Choose a service package — each property is booked separately. Switch the property above to book for another one.")
            : (de
                ? "Wählen Sie ein Betreuungspaket für Ihre Immobilie und buchen Sie es verbindlich."
                : "Choose a service package for your property and book it bindingly.")}
        </p>
      </div>

      {/* Objekt-spezifische Buchung — Umschalter blendet sich bei nur einem Objekt aus. */}
      <PropertySwitcher testidPrefix="service-packages-property-switcher" />

      {lastBooking && (
        <div
          className="mb-8 bg-white border-l-2 border-gold p-5 md:p-6"
          data-testid="service-packages-last-booking"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="label-caps mb-1.5">
                {de ? "Zuletzt gebucht" : "Last booked"}
                {multi && propName ? ` · ${propName}` : ""}
              </div>
              <p className="text-[14px] text-navy leading-relaxed">
                <span className="font-medium">{lbPkg ? pkgTitle(lbPkg) : lastBooking.package_title}</span> — {lbPkg ? pkgMain(lbPkg) : lastBooking.package_main}
                <span className="text-navy-dark/60"> · {deDate(lastBooking.created_at)}</span>
              </p>
              {lastBooking.start_date && (
                <p className="text-[13px] text-navy-dark/70 mt-1.5" data-testid="service-packages-last-booking-period">
                  {lastBooking.end_date
                    ? (de ? `Betreuung vom ${deDate(lastBooking.start_date)} bis ${deDate(lastBooking.end_date)}` : `Care from ${deDate(lastBooking.start_date)} to ${deDate(lastBooking.end_date)}`)
                    : (de ? `Betreuung ab ${deDate(lastBooking.start_date)} · fortlaufend` : `Care from ${deDate(lastBooking.start_date)} · ongoing`)}
                  {lastBooking.status === "cancelled" && (
                    <span className="ml-1 font-medium" style={{ color: STATUS_COLORS.red }} data-testid="service-packages-last-booking-cancelled">
                      · {de ? "gekündigt" : "cancelled"}
                    </span>
                  )}
                </p>
              )}
            </div>
            {canCancelLast && (
              <button
                type="button"
                onClick={() => setCancelTarget(lastBooking)}
                data-testid="service-packages-cancel-button"
                className="shrink-0 inline-flex items-center gap-2 border px-4 py-2 text-[11px] tracking-[0.14em] uppercase transition-colors hover:bg-red-50"
                style={{ borderColor: STATUS_COLORS.red, color: STATUS_COLORS.red }}
              >
                <XCircle className="w-4 h-4" strokeWidth={1.8} />
                {de ? "Paket kündigen" : "Cancel package"}
              </button>
            )}
          </div>
        </div>
      )}

      <ServicePackageBooker
        bookings={bookings}
        absences={absences}
        propertyId={selected?.id}
        propertyName={propName}
        multi={multi}
        de={de}
        presetPackageId={presetPackageId}
        onBooked={loadMine}
        testidPrefix="service-packages"
      />

      <p className="mt-6 text-[13px] text-navy-dark/70 leading-relaxed max-w-2xl" data-testid="service-packages-addon-hint">
        {de
          ? "Benötigen Sie Zusatzleistung, buchen Sie diese gleich mit. Schauen Sie dazu in den Bereich Zusatzleistungen."
          : "If you need add-on services, simply book them too. Take a look at the add-on services section."}
      </p>

      <p className="mt-4 text-[12px] text-navy-dark/60" data-testid="service-packages-footnote">
        {de ? "* Abrechnungszyklus monatlich" : "* Billing cycle monthly"}
      </p>

      {cancelTarget && (
        <ServicePackageCancelDialog
          booking={cancelTarget}
          isAdmin={false}
          de={de}
          onClose={() => setCancelTarget(null)}
          onCancelled={loadMine}
        />
      )}
    </div>
  );
}
